import React, { useState } from "react";
import { getAuth, signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { initializeApp } from "firebase/app";
import { useNavigate } from "react-router-dom";
import { getBackendErrorMessage, getFirebaseErrorMessage } from "../../utils/firebaseErrors";
import { BACKEND_URL } from "../../utils/config";
import "./Login.css";

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || "/backend";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState(null);
  const [isError, setIsError] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState(null);
  const [resetIsError, setResetIsError] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setMessage(null);
    setIsError(false);

  const firebaseConfig = process.env.REACT_APP_FIREBASE === 'development' ? {
    apiKey: "AIzaSyCk2KLEPKnILl9SJ7lT9frBdKS0o9Dv560",
    authDomain: "connection-healthcare-app.firebaseapp.com",
    projectId: "connection-healthcare-app",
    storageBucket: "connection-healthcare-app.firebasestorage.app",
    messagingSenderId: "559258061369",
    appId: "1:559258061369:web:7d9ca98231cb58a93fdbe8",
    measurementId: "G-DKPC3TR8YZ",
  } : {
    apiKey: "AIzaSyDPPnT8Ms89no1-hDXqbQsZrEI5UDEouzA",
    authDomain: "connection-healthcare-app-prod.firebaseapp.com",
    projectId: "connection-healthcare-app-prod",
    storageBucket: "connection-healthcare-app-prod.firebasestorage.app",
    messagingSenderId: "206198908104",
    appId: "1:206198908104:web:0d62c4a95fa7d4302631fd"
  };

    try {
      initializeApp(firebaseConfig);
      const auth = getAuth();
      // Sign in the user with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

//...
      // Optionally, fetch user-specific data from your backend
      const idToken = await userCredential.user.getIdToken();

      const res = await fetch(`${BACKEND_URL}/users/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          user_id: userCredential.user.uid,
          jwt_token: idToken,
        }),
      });

      
      if (res.status === 200) {
        const data = await res.json();
        // Store user data in localStorage
        localStorage.setItem('username', data.username || '');
        localStorage.setItem('user_id', userCredential.user.uid);
        localStorage.setItem('role', data.role || '');

        setMessage("Login successful! Redirecting to dashboard...");
        if (data.role === "psw") {
          setTimeout(() => navigate(`${FRONTEND_URL}/service-worker-dashboard`), 500);
        } else if (data.role === "admin") {
          setTimeout(() => navigate(`${FRONTEND_URL}/admin-dashboard`), 500);
        } else {
          setTimeout(() => navigate(`${FRONTEND_URL}/dashboard`), 500);
        }
        return;
      }

      if (!res.ok) {
        const text = await res.text();
        const errorMessage = getBackendErrorMessage(res.status, text);
        throw new Error(errorMessage);
      }

      setMessage(`Welcome back, ${data.username || "User"}!`);
      setIsError(false);
    } catch (err) {
      let errorMessage;
      
      // Check if it's a Firebase auth error (err.code or err.message contains auth/)
      if (err.code && err.code.startsWith("auth/")) {
        // Direct Firebase error object
        errorMessage = getFirebaseErrorMessage(err);
      } else if (err.message && err.message.includes("auth/")) {
        // Firebase error wrapped in Error message like "Firebase: Error (auth/user-not-found)"
        // Extract the error code from the message
        const match = err.message.match(/auth\/[\w\-]+/);
        if (match) {
          const firebaseErr = { code: match[0] };
          errorMessage = getFirebaseErrorMessage(firebaseErr);
        } else {
          errorMessage = err.message.replace(/Firebase:\s*Error\s*\(/, "").replace(/\)/, "");
        }
      } else if (err instanceof Error) {
        // It's a regular error (likely from our backend error handling)
        errorMessage = err.message;
      } else {
        // Fallback for unknown errors
        errorMessage = err?.message || "Login failed. Please try again.";
      }
      
      setMessage(errorMessage);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetMessage(null);
    setResetIsError(false);
    setResetLoading(true);

    if (!resetEmail) {
      setResetMessage("Please enter your email address");
      setResetIsError(true);
      setResetLoading(false);
      return;
    }

    const firebaseConfig = process.env.REACT_APP_FIREBASE === 'development' ? {
      apiKey: "AIzaSyCk2KLEPKnILl9SJ7lT9frBdKS0o9Dv560",
      authDomain: "connection-healthcare-app.firebaseapp.com",
      projectId: "connection-healthcare-app",
      storageBucket: "connection-healthcare-app.firebasestorage.app",
      messagingSenderId: "559258061369",
      appId: "1:559258061369:web:7d9ca98231cb58a93fdbe8",
      measurementId: "G-DKPC3TR8YZ",
    } : {
      apiKey: "AIzaSyDPPnT8Ms89no1-hDXqbQsZrEI5UDEouzA",
      authDomain: "connection-healthcare-app-prod.firebaseapp.com",
      projectId: "connection-healthcare-app-prod",
      storageBucket: "connection-healthcare-app-prod.firebasestorage.app",
      messagingSenderId: "206198908104",
      appId: "1:206198908104:web:0d62c4a95fa7d4302631fd"
    };

    try {
      initializeApp(firebaseConfig);
      const auth = getAuth();

      const USE_FAKE_DATA = process.env.REACT_APP_USE_FAKE_DATA;
      if (USE_FAKE_DATA) {
        const continueUrl = `https://localhost${FRONTEND_URL}/users/login`;
        await sendPasswordResetEmail(auth, resetEmail);
      } else {
        const continueUrl = `https://${BACKEND_URL}/users/login`;
              
        await sendPasswordResetEmail(auth, resetEmail, {
          url: continueUrl,
          handleCodeInApp: true,
        });
      }
      
      setResetMessage(`Password reset email sent to ${resetEmail}. Check your inbox for instructions.`);
      setResetIsError(false);
      setResetEmail("");
      setTimeout(() => {
        setShowResetModal(false);
      }, 3000);
    } catch (err) {
      let errorMessage;
      // Check if it's a Firebase auth error (err.code or err.message contains auth/)
      if (err.code && err.code.startsWith("auth/")) {
        // Direct Firebase error object
        errorMessage = getFirebaseErrorMessage(err);
      } else if (err.message && err.message.includes("auth/")) {
        // Firebase error wrapped in Error message like "Firebase: Error (auth/user-not-found)"
        // Extract the error code from the message
        const match = err.message.match(/auth\/[\w\-]+/);
        if (match) {
          const firebaseErr = { code: match[0] };
          errorMessage = getFirebaseErrorMessage(firebaseErr);
        } else {
          errorMessage = err.message.replace(/Firebase:\s*Error\s*\(/, "").replace(/\)/, "");
        }
      } else if (err instanceof Error) {
        // It's a regular error
        errorMessage = err.message;
      } else {
        // Fallback for unknown errors
        errorMessage = "Failed to send password reset email. Please try again.";
      }
      
      setResetMessage(errorMessage);
      setResetIsError(true);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="login-container">
      <form className="login-form" onSubmit={submit}>
        <h1>Login</h1>
        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password:</label>
          <input
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            required
          />
        </div>
        <button type="submit" className="login-form-button" disabled={loading}>
          {loading ? 'Logging in...' : 'Login'}
        </button>
        <button
          type="button"
          className="create-account-btn"
          onClick={() => navigate(`${FRONTEND_URL}/create`)}
          disabled={loading}
        >
          Create Account
        </button>
        <button
          type="button"
          className="forgot-password-btn"
          onClick={() => setShowResetModal(true)}
        >
          Forgot Password?
        </button>
      </form>

      {message && (
        <p className={`message ${isError ? "error" : "success"}`}>{message}</p>
      )}

      {showResetModal && (
        <div className="modal-overlay" onClick={() => setShowResetModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button
                className="modal-close"
                onClick={() => setShowResetModal(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <form onSubmit={handlePasswordReset} className="reset-form">
              <div className="form-group">
                <label htmlFor="reset-email">Email Address:</label>
                <input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                />
              </div>
              {resetMessage && (
                <p className={`message ${resetIsError ? "error" : "success"}`}>
                  {resetMessage}
                </p>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-button-cancel"
                  onClick={() => setShowResetModal(false)}
                  disabled={resetLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-button-submit"
                  disabled={resetLoading}
                >
                  {resetLoading ? "Sending..." : "Send Reset Email"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}